import { Component, OnInit, Optional } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { QuotationTypesEnums } from 'src/app/enum/quotation-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { QuotationVersionModel } from 'src/app/models/preliminaries/quotation-version.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { QuotationDetailSelectionTableModel } from 'src/app/models/tables/preliminaries/quotation-detail-selection-table.model';
import { QuotationSelectionTableModel } from 'src/app/models/tables/preliminaries/quotation-selection-table.model';
import { QuotationDetailService } from 'src/app/services/preliminaries/quotation-detail.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-copy-quotation',
  templateUrl: './copy-quotation.component.html',
  styleUrls: ['./copy-quotation.component.scss'],
  standalone: false
})
export class CopyQuotationComponent implements OnInit {
  ascendant: boolean = true;
  ascendantSolution: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Agregar',
      icon: '',
      color: ActionColor.SUCCESS,
      action: 'add'
    },
  ];

  columnSort: string | null = null;
  columnSortSolution: string | null = null;
  currentStep: number = 1;
  dataQuotation: PagedResponseModel<QuotationSelectionTableModel> = new PagedResponseModel<QuotationSelectionTableModel>();
  dataQuotationDetail: PagedResponseModel<QuotationDetailSelectionTableModel> = new PagedResponseModel<QuotationDetailSelectionTableModel>();
  filters: ColumnResponseModel[] = [];
  limit: number = 10;
  limitSolution: number = 10;
  versionModel: QuotationVersionModel = new QuotationVersionModel();
  page: number = 1;
  pageSolution: number = 1;
  quotationType: number = 1;
  quotationDetailHeader: ColumnResponseModel[] = [];
  quotationDetailId: number | null = null;
  quotationHeader: ColumnResponseModel[] = [];
  quotationId: number | null = null;
  quotationTypeIdSelected: number = 0;
  suscription: Subscription;

  constructor(
    public config: DynamicDialogConfig,
    private quotationDetailService: QuotationDetailService,
    private quotationService: QuotationService,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef,
  ) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.versionModel = inputs.quotationVersion;
      this.quotationType = inputs.quotationType;
    });
    await this.loadDataForStep(1);
  }

  async loadDataForStep(step: number) {
    try {
      switch (step) {
        case 1:
          await Promise.all([
            this.getColumnsForQuotation(),
            this.getQuotationsForSelection()
          ]);
          break;
        case 2:
          if (this.quotationId) {
            await Promise.all([
              this.getColumnsForQuotationDetail(),
              this.getQuotationDetailsForSelection()
            ]);
          }
          break;
      }
    } catch (error) {
      console.error(`Error cargando datos.`, error);
    }
  }

  async getColumnsForQuotation() {
    try {
      this.quotationHeader = await firstValueFrom(this.quotationService.getColumnsForQuotationSelection());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForQuotationDetail() {
    try {
      this.quotationDetailHeader = await firstValueFrom(this.quotationDetailService.getColumnsForDetailByVersionsSelectionAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getQuotationById(quotationId: number) {
    try {
      const quotation = await firstValueFrom(this.quotationService.getQuotationById(quotationId));
      this.quotationId = quotation.quotationId;
      this.quotationTypeIdSelected = quotation.quotationTypeId;
    } catch (error) {
      console.error('Error al obtener la cotización.', error);
    }
  }

  async getQuotationsForSelection() {
    try {
      this.dataQuotation = await firstValueFrom(this.quotationService.getQuotationsForSelection(this.page, this.limit,
        this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al obtener la lista de los artículos.', error);
    }
  }

  async getQuotationDetailsForSelection() {
    try {
      this.dataQuotationDetail = await firstValueFrom(this.quotationDetailService.getDetailByVersionsForSelectionAsync(this.pageSolution, this.limitSolution,
        this.columnSortSolution, this.ascendantSolution, this.filters, this.quotationId ?? 0));
    } catch (error) {
      this.dataQuotationDetail = new PagedResponseModel<QuotationDetailSelectionTableModel>();
      console.error('Error al obtener los detalles de cotización.', error);
    }
  }

  async goToStep(step: number, activateCallback: any) {
    if (step < this.currentStep) { this.clearFutureStepSelections(step); }
    if (!this.validateCurrentStepSelection(step)) { return; }
    await this.loadDataForStep(step);
    this.currentStep = step;
    activateCallback(step);


  }

  private clearFutureStepSelections(currentStep: number) {
    switch (currentStep) {
      case 1:
        this.quotationId = null;
        break;
      case 2:
        this.quotationDetailId = null;
        break;
    }
  }

  private async copySolutionFromQuotation(selectedItems: number[]) {
    Swal.fire({
      text: '¿Está seguro que desea copiar las soluciones seleccionadas?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await firstValueFrom(this.quotationDetailService.copySolutionFromQuotation(selectedItems, this.versionModel.quotationId, this.versionModel.quotationVersionId));
          if (response) {
            const output$ = this.config.data?.output$;
            output$?.next({ type: 'updateList', payload: true });
            this.toastHandlerService.showToast('success', 'Las soluciones se copiaron exitosamente.', '');
          }
          if (this.ref) this.ref.close();
        } catch (error) {
          console.error('Error al crear el pedido.', error);
        }
      } else {
        this.toastHandlerService.showToast('warning', 'Proceso de copiar solucón cancelado.', '');
      }
    });
  }

  getNameQuotationType(quotationTypeId: number): string {
    let typeName: string;
    switch (quotationTypeId) {
      case QuotationTypesEnums.rental:
        typeName = 'Alquiler';
        break;
      case QuotationTypesEnums.newSale:
        typeName = 'Venta Nuevo';
        break;
      case QuotationTypesEnums.usedSale:
        typeName = 'Venta Usado';
        break;
      case QuotationTypesEnums.service:
        typeName = 'Servicio';
        break;
      case QuotationTypesEnums.maintenance:
        typeName = 'Mantenimiento';
        break;
      default:
        typeName = 'Desconocido';
    }
    return typeName;
  }

  async onAction(event: { action: string, item?: QuotationDetailSelectionTableModel, selectedItems?: number[] }) {
    if (event.action == 'add' && event.selectedItems) {
      await this.copySolutionFromQuotation(event.selectedItems);
    } else {
      console.error('No se seleccionaron items.');
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async selectionChange(event: { selectedItem?: any }) {
    const selectedId = event?.selectedItem?.id;
    if (!selectedId) {
      this.clearFutureStepSelections(this.currentStep);
      return;
    }
    switch (this.currentStep) {
      case 1:
        await this.getQuotationById(selectedId);
        break;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {

    switch (this.currentStep) {
      case 1:
        this.page = event.page;
        this.limit = event.pageSize;
        this.columnSort = event.sortField || null;
        this.ascendant = event.sortOrder === 1;
        await this.getQuotationsForSelection()
        break;
      case 2:
        this.pageSolution = event.page;
        this.limitSolution = event.pageSize;
        this.columnSortSolution = event.sortField || null;
        this.ascendantSolution = event.sortOrder === 1;
        await this.getQuotationDetailsForSelection()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  validateCurrentStepSelection(step: number): boolean {
    switch (step) {
      case 2:
        if (!this.quotationId) {
          this.toastHandlerService.showToast('warning', 'Debe seleccionar una cotización válida antes de avanzar a la solución.', 'Atención');
          return false;
        } else if (this.quotationType != this.quotationTypeIdSelected) {
          const typeName = this.getNameQuotationType(this.quotationType);
          const typeNameSelected = this.getNameQuotationType(this.quotationTypeIdSelected);
          this.toastHandlerService.showToast('warning', `No es posible continuar: la cotización actual es "${typeName}" y seleccionó una de tipo "${typeNameSelected}"`, '');
          return false;
        }
        break;
    }
    return true;
  }
}